// components/BillPayment.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { apiCall } from "../utils/api";
import { APP_CONFIG } from "../app-config";

const COLORS = APP_CONFIG.colors;

// All available categories
const ALL_CATEGORIES = [
    "Electricity",
    "Fastag",
    "Gas",
    "Broadband Postpaid",
    "Cable TV",
    "Clubs and Associations",
    "Credit Card",
    "Education Fees",
    "Health Insurance",
    "Hospital",
    "Hospital and Pathology",
    "Housing Society",
    "Insurance",
    "Landline Postpaid",
    "Life Insurance",
    "Loan Repayment",
    "LPG Gas",
    "Mobile Postpaid",
    "Municipal Services",
    "Municipal Taxes",
    "Postpaid",
    "Recurring Deposit",
    "Subscription",
    "Water",
    "Donation",
    "Prepaid Meter"
];

// Helper function to get color based on biller name
const getBillerColor = (billerName) => {
    const colors = ["#0277BD", "#01579B", "#006064", "#004D40", "#1B5E20", "#E65100", "#B71C1C", "#311B92", "#4527A0", "#F57F17", "#0D47A1", "#BF360C", "#2E7D32", "#C62828", "#283593"];
    let hash = 0;
    for (let i = 0; i < billerName.length; i++) {
        hash = billerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
};

const getBillerLogo = (billerName) => {
    const words = billerName.split(' ');
    if (words.length === 1) {
        return billerName.substring(0, 2).toUpperCase();
    }
    return (words[0].charAt(0) + (words[1].charAt(0) || words[0].charAt(1))).toUpperCase();
};

function BillPayment({ category }) {
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = useState(category || "");
    const [selectedOperator, setSelectedOperator] = useState("");
    const [operators, setOperators] = useState([]);
    const [loadingOperators, setLoadingOperators] = useState(false);
    const [error, setError] = useState("");

    // Operator Form States
    const [formData, setFormData] = useState({});
    const [isFetching, setIsFetching] = useState(false);
    const [step, setStep] = useState("input");
    const [formFields, setFormFields] = useState([]);
    const [loadingForm, setLoadingForm] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [inputSLNumber, setInputSLNumber] = useState("");
    const [selectedOperatorData, setSelectedOperatorData] = useState(null);

    // Fetch operators when category changes
    useEffect(() => {
        if (!selectedCategory) {
            setOperators([]);
            setSelectedOperator("");
            setSelectedOperatorData(null);
            resetForm();
            return;
        }

        const fetchOperators = async () => {
            setLoadingOperators(true);
            setOperators([]);
            setSelectedOperator("");
            setSelectedOperatorData(null);
            resetForm();

            try {
                const result = await apiCall(
                    "operator",
                    {
                        category: selectedCategory
                    },
                    true
                );

                console.log("Operators Response:", result);

                if (result.status === true && result.response_code === 1) {
                    setOperators(result.data || []);
                    if (result.data?.length === 0) {
                        toast.error(`No operators found for ${selectedCategory}`);
                    }
                } else {
                    toast.error(result.message || `Failed to fetch ${selectedCategory} operators`);
                    setOperators([]);
                }
            } catch (error) {
                console.error("API Error:", error);
                toast.error("Network error. Please try again.");
                setOperators([]);
            } finally {
                setLoadingOperators(false);
            }
        };

        fetchOperators();
    }, [selectedCategory]);

    // Fetch operator form when operator is selected
    useEffect(() => {
        if (!selectedOperator) {
            resetForm();
            return;
        }

        const fetchOperatorForm = async () => {
            setLoadingForm(true);
            setFormFields([]);
            setFormData({});
            setIsFormValid(false);
            setInputSLNumber("");

            try {
                const result = await apiCall(
                    "operator-form",
                    {
                        operator_id: selectedOperator
                    },
                    true
                );

                console.log("Operator Form Response:", result);

                if (result?.status !== true || result?.response_code !== 1) {
                    toast.error(result?.message || "Failed to load form data");
                    return;
                }

                // Support different possible API response structures
                const operatorForm =
                    result?.operator_form ??
                    result?.data?.operator_form ??
                    result?.data ??
                    [];

                // Always convert to array
                const forms = Array.isArray(operatorForm)
                    ? operatorForm
                    : operatorForm
                        ? [operatorForm]
                        : [];

                console.log("Normalized Forms:", forms);

                if (forms.length === 0) {
                    toast.error("No form configuration found");
                    return;
                }

                const form = forms[0];

                console.log("Selected Form:", form);
                console.log("nparam:", form?.nparam);

                setInputSLNumber(form?.sl || "");

                initializeFormFields(form);

            } catch (error) {
                console.error("Error fetching operator form:", error);
                toast.error("Failed to load form data");
            } finally {
                setLoadingForm(false);
            }
        };

        fetchOperatorForm();
    }, [selectedOperator]);

    // Validate form whenever formData or formFields change
    useEffect(() => {
        validateForm();
    }, [formData, formFields]);

    const resetForm = () => {
        setFormData({});
        setFormFields([]);
        setStep("input");
        setIsFormValid(false);
        setInputSLNumber("");
    };

    const initializeFormFields = (form) => {
        if (!form) {
            console.log("No form received");
            setFormFields([]);
            setFormData({});
            return;
        }

        const nparam = parseInt(form.nparam, 10) || 0;

        console.log("Initializing form:", form);
        console.log("Number of parameters:", nparam);

        const dynamicFields = [];
        const initialFormData = {};

        for (let i = 1; i <= nparam; i++) {
            const paramKey = `param${i}`;

            const label = form[`${paramKey}_label`] || "";
            const type = form[`${paramKey}_type`] || "Alphanumeric";
            const value = form[`${paramKey}_value`] ?? "";

            const isOptional = String(
                form[`${paramKey}_isOptional`] ?? ""
            );

            const isHidden = String(
                form[`${paramKey}_isHidden`] ?? "0"
            );

            const min = parseInt(
                form[`${paramKey}_min`] || "0",
                10
            );

            const max = parseInt(
                form[`${paramKey}_max`] || "0",
                10
            );

            const regex =
                form[`${paramKey}_regular_ex`] || "";

            const isPrimary =
                String(form[`${paramKey}_isPrimary`] ?? "0") === "1";

            const isAdditional =
                String(form[`${paramKey}_isAdditional`] ?? "0") === "1";

            console.log(`Processing ${paramKey}:`, {
                label,
                type,
                value,
                isOptional,
                isHidden,
                min,
                max,
                regex
            });

            // Hidden field
            if (isHidden === "1") {
                continue;
            }

            // If API says parameter exists but label is empty,
            // don't render it as a visible field.
            if (!label.trim()) {
                continue;
            }

            let fieldType = "text";

            switch (type.toLowerCase()) {
                case "number":
                case "numeric":
                    fieldType = "number";
                    break;

                case "date":
                    fieldType = "date";
                    break;

                case "email":
                    fieldType = "email";
                    break;

                case "phone":
                    fieldType = "tel";
                    break;

                case "dropdown":
                    fieldType = "select";
                    break;

                case "alphanumeric":
                default:
                    fieldType = "text";
                    break;
            }

            const field = {
                name: paramKey,
                key: paramKey,
                label: label,
                placeholder: `Enter ${label}`,
                type: fieldType,

                required: isOptional === "0",

                regex: regex.trim() || null,

                minLength: min,
                maxLength: max,

                value: value,

                isDropdown: fieldType === "select",
                options: [],

                isHidden: isHidden === "1",
                isPrimary,
                isAdditional,

                paramType: type,

                shouldUppercase:
                    fieldType === "text" ||
                    fieldType === "tel"
            };

            dynamicFields.push(field);

            initialFormData[paramKey] = value;
        }

        console.log("FINAL FORM FIELDS:", dynamicFields);
        console.log("FINAL FORM DATA:", initialFormData);

        setFormFields(dynamicFields);
        setFormData(initialFormData);
    };

    const validateForm = () => {
        let isValid = true;

        if (formFields.length === 0) {
            setIsFormValid(false);
            return;
        }

        for (const field of formFields) {
            const value = formData[field.key] || '';
            const valueString = String(value);

            if (field.required && !valueString.trim()) {
                isValid = false;
                break;
            }

            if (field.isDropdown && field.required && (!valueString || valueString.trim() === '')) {
                isValid = false;
                break;
            }

            if (!field.required && !valueString.trim()) {
                continue;
            }

            if (field.regex && valueString && field.regex.trim() !== "") {
                try {
                    const regex = new RegExp(field.regex);
                    if (!regex.test(valueString)) {
                        isValid = false;
                        break;
                    }
                } catch (error) {
                    console.error('Invalid regex pattern:', field.regex);
                }
            }

            if (!field.isDropdown && field.type !== "number" && field.type !== "date") {
                if (field.minLength > 0 && valueString.length < field.minLength) {
                    isValid = false;
                    break;
                }
                if (field.maxLength > 0 && valueString.length > field.maxLength) {
                    isValid = false;
                    break;
                }
            }

            if (field.type === "number" && valueString) {
                const numValue = parseFloat(valueString);
                if (isNaN(numValue)) {
                    isValid = false;
                    break;
                }
                const digitLength = valueString.replace(/^0+/, '').length || valueString.length;
                if (field.minLength > 0 && digitLength < field.minLength) {
                    isValid = false;
                    break;
                }
                if (field.maxLength > 0 && digitLength > field.maxLength) {
                    isValid = false;
                    break;
                }
            }
        }

        setIsFormValid(isValid);
    };

    const handleInputChange = (key, value, shouldUppercase = false) => {
        let processedValue = value;
        if (shouldUppercase && typeof value === 'string') {
            processedValue = value.toUpperCase();
        }

        setFormData(prev => ({
            ...prev,
            [key]: processedValue
        }));
    };

    const handleCategoryChange = (e) => {
        const category = e.target.value;
        setSelectedCategory(category);
        setSelectedOperator("");
        setSelectedOperatorData(null);
        setError("");
        resetForm();
    };

    const handleOperatorChange = (e) => {
        const operatorId = e.target.value;

        setSelectedOperator(operatorId);
        setError("");

        const selectedOp = operators.find(
            op => String(op.operator_id) === String(operatorId)
        );

        console.log("Selected Operator:", selectedOp);

        setSelectedOperatorData(selectedOp || null);
    };

    const handleFetchBill = async () => {
        if (!isFormValid) {
            toast.error("Please fill all required fields correctly");
            return;
        }

        setIsFetching(true);

        try {
            const result = await apiCall(
                "bill-fetch",
                {
                    operator_id: selectedOperator,
                    form: formData,
                    input_sl_number: inputSLNumber,
                },
                true
            );

            if (result.status === true && result.response_code === 1) {
                const billInfo = result.bill_info || {};
                if (billInfo.acceptPayment) {
                    var additional = {};

                    const additionalInfo = result.additional?.bill_fetch_info?.additionalInfo?.info || [];
                    if (Array.isArray(additionalInfo) && additionalInfo.length > 0) {
                        additional = additionalInfo;
                    }

                    navigate("/payment-confirmation", {
                        state: {
                            service: "Bill Payment",
                            operator: selectedOperatorData?.biller_name || "",
                            operator_id: selectedOperator,
                            logo: result.logo,
                            number: billInfo.cellNumber || "",
                            amount: billInfo.billnetamount || result.range?.min || 100,
                            consumerName: billInfo.userName || "",
                            serviceSpecificData: {
                                range: result.range,
                                acceptPartPay: billInfo.acceptPartPay !== false,
                                billNumber: "",
                                billDate: billInfo.billDate || "",
                                dueDate: billInfo.dueDate || "",
                                billAmount: billInfo.billAmount || "",
                                billConvenienceFee: billInfo.bill_convenience_fee || "",
                                billNetAmount: billInfo.billnetamount || result.range?.min || 100,
                                additional: additional,
                                pass_data: result.additional,
                                form: formData
                            }
                        }
                    });
                } else {
                    toast.error(result.message || "Failed to fetch bill");
                }
            } else {
                toast.error(result.message || "Failed to fetch bill");
            }
        } catch (error) {
            console.error("Fetch bill error:", error);
            toast.error("Network error. Please try again.");
        } finally {
            setIsFetching(false);
        }
    };

    const renderFormField = (field) => {
        console.log("Rendering field:", field.key, field.type, field.label);

        if (field.isDropdown && field.options && field.options.length > 0) {
            return (
                <div className="relative">
                    <select
                        value={formData[field.key] || ''}
                        onChange={(e) => handleInputChange(field.key, e.target.value, false)}
                        className="w-full p-3.5 rounded-xl text-[15px] font-semibold outline-none transition-all duration-200 appearance-none cursor-pointer"
                        style={{
                            border: `1px solid ${COLORS.borderColor}`,
                            background: COLORS.surfaceColor,
                            color: COLORS.textPrimary,
                        }}
                        required={field.required}
                    >
                        <option value="" disabled>
                            Select {field.label}
                        </option>
                        {field.options.map((option, index) => (
                            <option key={index} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <div
                        className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none"
                        style={{ color: COLORS.textSecondary }}
                    >
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                        </svg>
                    </div>
                </div>
            );
        }

        const inputProps = {
            type: field.type,
            value: formData[field.key] || '',
            onChange: (e) => handleInputChange(field.key, e.target.value, field.shouldUppercase),
            placeholder: field.placeholder,
            className: `w-full p-3.5 rounded-xl text-[15px] font-semibold outline-none transition-all duration-200`,
            style: {
                border: `1px solid ${COLORS.borderColor}`,
                background: COLORS.surfaceColor,
                color: COLORS.textPrimary,
                textTransform: field.shouldUppercase ? 'uppercase' : 'none'
            },
            onFocus: (e) => (e.target.style.borderColor = COLORS.primaryColor),
            onBlur: (e) => (e.target.style.borderColor = COLORS.borderColor),
            required: field.required
        };

        if (field.type === "number") {
            if (field.minLength > 0) inputProps.min = field.minLength;
            if (field.maxLength > 0) inputProps.max = field.maxLength;
        }

        if (field.type === "text" || field.type === "email" || field.type === "tel") {
            if (field.minLength > 0) inputProps.minLength = field.minLength;
            if (field.maxLength > 0) inputProps.maxLength = field.maxLength;
        }

        if (field.regex && field.regex.trim() !== "" && field.type !== "date" && field.type !== "number") {
            inputProps.pattern = field.regex;
        }

        return <input {...inputProps} />;
    };

    const renderOperatorForm = () => {
        if (!selectedOperatorData) return null;

        const info = {
            logo: selectedOperatorData.logo ? null : getBillerLogo(selectedOperatorData.biller_name),
            color: getBillerColor(selectedOperatorData.biller_name)
        };

        if (loadingForm) {
            return (
                <div className="mt-6 pt-6 border-t" style={{ borderColor: COLORS.borderColor }}>
                    <div className="text-center py-8">
                        <div className="w-10 h-10 rounded-full animate-spin mx-auto mb-4" style={{ border: `3px solid ${COLORS.primaryColor}33`, borderTopColor: COLORS.primaryColor }} />
                        <div className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
                            Loading form fields...
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="mt-6 pt-6 border-t" style={{ borderColor: COLORS.borderColor }}>
                {/* Operator Info Card */}
                <div className="flex items-center gap-3 mb-6 p-3.5 px-4 rounded-xl border"
                    style={{
                        background: COLORS.surfaceColor,
                        borderColor: COLORS.borderColor,
                    }}
                >
                    {selectedOperatorData.logo ? (
                        <img
                            src={selectedOperatorData.logo}
                            alt={selectedOperatorData.biller_name}
                            className="w-11 h-11 rounded-xl object-cover flex-shrink-0"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                }
                            }}
                        />
                    ) : null}
                    <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center text-[14px] font-extrabold text-white flex-shrink-0"
                        style={{
                            background: info.color,
                            display: selectedOperatorData.logo ? 'none' : 'flex'
                        }}
                    >
                        {info.logo}
                    </div>
                    <div className="flex-1">
                        <div className="text-[15px] font-bold" style={{ color: COLORS.textPrimary }}>
                            {selectedOperatorData.biller_name}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Dynamic Form Fields */}
                    {formFields.length > 0 ? (
                        <>
                            {formFields.map((field) => (
                                <div key={field.key} className="mb-5">
                                    <label className="block text-sm font-semibold mb-2" style={{ color: COLORS.textPrimary }}>
                                        {field.label}
                                        {field.required && <span style={{ color: '#EF4444' }}> *</span>}
                                        {field.minLength > 0 && field.maxLength > 0 && field.minLength === field.maxLength && (
                                            <span className="text-xs ml-2" style={{ color: COLORS.textSecondary }}>
                                                ({field.minLength} characters)
                                            </span>
                                        )}
                                    </label>
                                    {renderFormField(field)}
                                </div>
                            ))}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={{ color: COLORS.textSecondary }}>
                                    Action
                                </label>
                                {/* Fetch Bill Button */}
                                <button
                                    onClick={handleFetchBill}
                                    disabled={isFetching || !isFormValid}
                                    className="w-full h-12 text-white border-none rounded-xl text-base font-bold cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{
                                        background: COLORS.primaryColor,
                                    }}
                                >
                                    {isFetching ? (
                                        <>
                                            <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `2px solid #ffffff`, borderTopColor: "transparent" }} />
                                            <span>Fetching Bill...</span>
                                        </>
                                    ) : (
                                        "Fetch Bill"
                                    )}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="text-sm" style={{ color: COLORS.textSecondary }}>
                                No input fields available for this operator
                            </div>
                            <div className="text-xs mt-2" style={{ color: COLORS.textSecondary }}>
                                Form fields: {JSON.stringify(formFields)}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div
                className="rounded-2xl p-4 md:p-6"
                style={{
                    background: COLORS.bgColor,
                    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                    border: `1px solid ${COLORS.borderColor}`,
                }}
            >
                <h3 className="text-lg font-semibold mb-4" style={{ color: COLORS.textPrimary }}>
                    Bill Payment
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Category Select */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: COLORS.textSecondary }}>
                            Select Bill Category
                        </label>
                        <select
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all duration-200 appearance-none"
                            style={{
                                background: COLORS.surfaceColor,
                                borderColor: error && !selectedCategory ? '#EF4444' : COLORS.borderColor,
                                color: COLORS.textPrimary,
                                '--tw-ring-color': COLORS.primaryColor,
                            }}
                        >
                            <option value="">Select Category</option>
                            {ALL_CATEGORIES.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Operator Select */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: COLORS.textSecondary }}>
                            Select Operator
                        </label>
                        <select
                            value={selectedOperator}
                            onChange={handleOperatorChange}
                            disabled={!selectedCategory || loadingOperators}
                            className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all duration-200 appearance-none"
                            style={{
                                background: selectedCategory ? COLORS.surfaceColor : `${COLORS.surfaceColor}80`,
                                borderColor: error && !selectedOperator ? '#EF4444' : COLORS.borderColor,
                                color: selectedCategory ? COLORS.textPrimary : COLORS.textSecondary,
                                '--tw-ring-color': COLORS.primaryColor,
                                cursor: selectedCategory ? 'pointer' : 'not-allowed',
                                opacity: selectedCategory ? 1 : 0.6,
                            }}
                        >
                            <option value="">
                                {loadingOperators
                                    ? "Loading operators..."
                                    : !selectedCategory
                                        ? "Select category first"
                                        : "Select Operator"}
                            </option>
                            {operators.map((op) => (
                                <option key={op.operator_id} value={op.operator_id}>
                                    {op.biller_name}
                                </option>
                            ))}
                        </select>
                        {selectedCategory && operators.length === 0 && !loadingOperators && (
                            <div className="text-xs mt-1.5" style={{ color: '#EF4444' }}>
                                No operators available for {selectedCategory}
                            </div>
                        )}
                    </div>
                </div>

                {/* Operator Form */}
                {selectedOperator && renderOperatorForm()}
            </div>

            <style>
                {`
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                    .animate-spin {
                        animation: spin 1s linear infinite;
                    }
                `}
            </style>
        </div>
    );
}

export default BillPayment;